/**
 * SharePoint REST API Client
 * Handles all SharePoint REST API operations including authentication, request digest,
 * and batch operations.
 */

// Import necessary dependencies
import { getSharePointAuthHeaders } from './auth.js';
import { SharePointList } from './list.js';
import { SharePointFiles } from './files.js';
import {
  getCurrentUser,
  getCurrentUserGroups,
  getAllGroups,
  addUserToGroup,
  removeUserFromGroup,
  isUserInGroup,
} from './user.js';
import { sendEmail } from './email.js';

/**
 * SharePoint batch size limit with safety margin
 * Actual limit is 1048596 bytes, using conservative value for safety
 */
const MAX_BATCH_SIZE_BYTES = 1048000;

/**
 * Conservative estimate for batch boundary overhead
 * Each part needs "--{boundary}\r\n" prefix (~50 bytes)
 * Plus final "--{boundary}--\r\n" (~52 bytes)
 */
const BATCH_BOUNDARY_OVERHEAD_PER_PART = 50;
const BATCH_ENVELOPE_OVERHEAD = 52;

/**
 * Returns the byte length of a string in UTF-8.
 * Uses TextEncoder when available, falls back to string length for ASCII-only.
 *
 * @param {string} str - The string to measure
 * @returns {number} Byte length
 */
const _byteLength =
  typeof TextEncoder !== 'undefined'
    ? (() => {
        const encoder = new TextEncoder();
        return str => encoder.encode(str).length;
      })()
    : str => str.length;

/**
 * Custom error class for SharePoint API errors with rich classification flags.
 */
export class SharePointError extends Error {
  /**
   * @param {string} message - Error message
   * @param {number} statusCode - HTTP status code
   * @param {Response} response - The raw fetch Response object
   * @param {object} [errorData=null] - Parsed error data from SharePoint
   */
  constructor(message, statusCode, response, errorData = null) {
    super(message);
    this.name = 'SharePointError';
    this.statusCode = statusCode;
    this.response = response;
    this.errorData = errorData;

    // Extract SharePoint-specific error info
    this.errorCode = this._extractErrorCode(errorData);
    this.errorType = this._extractErrorType(errorData);

    // Classification flags (default to false)
    this.isRetryable = false;
    this.isListViewThreshold = false;
    this.isDigestError = false;
    this.isPermissionDenied = false;
    this.isNotFound = false;
    this.isConflict = false;
    this.isVersionConflict = false;
    this.isThrottled = false;
    this.isAuthenticationError = false;
    this.isServerError = false;
    this.isBadRequest = false;
    this.isItemExists = false;

    // Classify error based on available information
    this._classify();
  }

  /**
   * Extracts SharePoint error code from error data.
   * @param {object} errorData - Parsed error data
   * @returns {string|null} Error code or null
   * @private
   */
  _extractErrorCode(errorData) {
    if (!errorData) return null;

    // Try different possible locations for error code
    const code =
      errorData['odata.error']?.code || errorData.error?.code || null;

    if (!code) return null;

    // Extract numeric code if present (e.g., "-2147024860, Microsoft.SharePoint.SPQueryThrottledException")
    const match = code.match(/-?\d+/);
    return match ? match[0] : null;
  }

  /**
   * Extracts SharePoint error type from error data.
   * @param {object} errorData - Parsed error data
   * @returns {string|null} Error type or null
   * @private
   */
  _extractErrorType(errorData) {
    if (!errorData) return null;

    const code =
      errorData['odata.error']?.code || errorData.error?.code || null;

    if (!code || typeof code !== 'string') return null;

    // Extract type after the comma (e.g., "Microsoft.SharePoint.SPQueryThrottledException")
    const parts = code.split(',');
    return parts.length > 1 ? parts[1].trim() : null;
  }

  /**
   * Classifies the error based on status code and SharePoint error code.
   * @private
   */
  _classify() {
    // Apply status code-based classification first
    const statusFlags = STATUS_CODE_MAP[this.statusCode];
    if (statusFlags) {
      Object.assign(this, statusFlags);
    }

    // Apply error code-based classification (overrides status code flags)
    if (this.errorCode) {
      const errorCodeFlags = ERROR_CODE_MAP[this.errorCode];
      if (errorCodeFlags) {
        Object.assign(this, errorCodeFlags);
      }
    }
  }
}

/**
 * Maps SharePoint error codes to classification flags.
 * @type {Object<string, object>}
 */
const ERROR_CODE_MAP = {
  '-2147024860': { isListViewThreshold: true, isRetryable: false },
  '-2147024891': { isPermissionDenied: true, isRetryable: false },
  '-1597308888': { isDigestError: true, isRetryable: true },
  '-2130575251': { isNotFound: true, isRetryable: false },
  '-2130575276': { isNotFound: true, isRetryable: false },
  '-2147024894': { isNotFound: true, isRetryable: false },
  '-2130246326': {
    isVersionConflict: true,
    isConflict: true,
    isRetryable: false,
  },
  '-2147023174': { isItemExists: true, isConflict: true, isRetryable: false },
  '-2147024809': { isBadRequest: true, isRetryable: false },
  '-2147024713': { isBadRequest: true, isRetryable: false },
  '-2147467259': { isServerError: true, isRetryable: true },
};

/**
 * Maps HTTP status codes to classification flags.
 * @type {Object<number, object>}
 */
const STATUS_CODE_MAP = {
  400: { isBadRequest: true, isRetryable: false },
  401: { isAuthenticationError: true, isRetryable: false },
  404: { isNotFound: true, isRetryable: false },
  409: { isConflict: true, isRetryable: false },
  429: { isThrottled: true, isRetryable: true },
  502: { isServerError: true, isRetryable: true },
  503: { isServerError: true, isRetryable: true },
};

export class SharePointClient {
  /**
   * Creates a new SharePoint client instance.
   *
   * @param {object} [options={}] - Configuration options
   * @param {string} [options.siteUrl] - The SharePoint site URL (auto-detected if not provided)
   * @param {Function} options.authProvider - Async function that returns authentication headers
   * @param {boolean} [options.useTransparentBatching=false] - Enable transparent batching (opt-in)
   * @param {number} [options.batchDelay=50] - Time in ms to wait before sending batch
   * @param {number} [options.maxBatchedOps=100] - Maximum operations per batch
   * @param {boolean} [options.debug=false] - Enable debug logging for batching
   */
  constructor(options = {}) {
    const siteUrl = options.siteUrl || this._autoDetectSiteUrl();

    if (!siteUrl) {
      throw new Error(
        'SharePointClient requires siteUrl option or must be running in SharePoint context'
      );
    }

    this.siteUrl = siteUrl.replace(/\/$/, '');
    this.authProvider = options.authProvider ?? getSharePointAuthHeaders;

    // Batching configuration
    this.useTransparentBatching = options.useTransparentBatching ?? false;
    this.batchDelay = options.batchDelay ?? 50;
    this.maxBatchedOps = options.maxBatchedOps ?? 100;
    this.debug = options.debug ?? false;

    // Internal state
    this._requestDigest = null;
    this._requestDigestExpiration = null;
    this._digestRequests = null;

    // Batching state
    this._batchQueue = [];
    this._batchTimer = null;
    this._batchSizeBytes = 0;

    // Per-client cache of SharePointList instances, keyed by listIdentifier.
    this._lists = new Map();
  }

  /**
   * Returns a (cached) SharePointList for the given identifier.
   * @param {string} listIdentifier - List title or GUID
   * @returns {SharePointList}
   */
  list(listIdentifier) {
    if (!listIdentifier) {
      throw new Error('list: listIdentifier is required');
    }
    let instance = this._lists.get(listIdentifier);
    if (!instance) {
      instance = new SharePointList({ listIdentifier, spClient: this });
      this._lists.set(listIdentifier, instance);
    }
    return instance;
  }

  /**
   * Creates a new SharePoint list and returns the list instance.
   * If the list already exists, returns the existing instance.
   *
   * @param {object} options
   * @param {string} options.title
   * @param {string} [options.description='']
   * @param {number} [options.baseTemplate=100] - 100 = Custom List
   * @returns {Promise<SharePointList>}
   */
  async createList(options) {
    const { title, description = '', baseTemplate = 100 } = options || {};
    if (!title) {
      throw new Error('createList: title is required');
    }

    try {
      await this.request(
        'web/lists',
        'POST',
        {
          body: JSON.stringify({
            __metadata: { type: 'SP.List' },
            BaseTemplate: baseTemplate,
            Title: title,
            Description: description,
          }),
        },
        true
      );
    } catch (error) {
      if (!error.message?.includes('already exists')) {
        throw error;
      }
      console.warn(`List '${title}' already exists. Returning existing list.`);
    }
    return this.list(title);
  }

  /**
   * Returns a SharePointFiles instance rooted at the given path.
   * @param {object} [options]
   * @param {string} [options.rootPath='']
   * @param {boolean} [options.defaultToLocal=false]
   * @returns {SharePointFiles}
   */
  files(options = {}) {
    return new SharePointFiles({ spClient: this, ...options });
  }

  // --- User & group operations -------------------------------------------

  /** @returns {Promise<{id: string, name: string, email: string}>} */
  getCurrentUser() {
    return getCurrentUser(this);
  }

  /** @returns {Promise<Array>} */
  getCurrentUserGroups() {
    return getCurrentUserGroups(this);
  }

  /** @returns {Promise<Array>} */
  getAllGroups() {
    return getAllGroups(this);
  }

  /**
   * @param {number} groupId
   * @param {string} loginName
   */
  addUserToGroup(groupId, loginName) {
    return addUserToGroup(this, groupId, loginName);
  }

  /**
   * @param {number} groupId
   * @param {string} loginName
   */
  removeUserFromGroup(groupId, loginName) {
    return removeUserFromGroup(this, groupId, loginName);
  }

  /**
   * @param {number} groupId
   * @returns {Promise<boolean>}
   */
  isUserInGroup(groupId) {
    return isUserInGroup(this, groupId);
  }

  // --- Email -------------------------------------------------------------

  /**
   * @param {object} emailOptions
   * @param {string|Array} emailOptions.to
   * @param {string} emailOptions.subject
   * @param {string} emailOptions.body
   * @param {string|Array} [emailOptions.cc]
   * @param {string|Array} [emailOptions.bcc]
   */
  sendEmail(emailOptions) {
    return sendEmail(this, emailOptions);
  }

  /**
   * Determines if an error comes from network (connection lost or server unreachable).
   *
   * @param {Error} error - The error to check
   * @returns {boolean} True if the error comes from network
   * @private
   */
  isNetworkError(error) {
    // Network errors (no response received)
    return error.name === 'TypeError' || error.message.includes('network');
  }

  /**
   * Determines if an error is retryable (network or digest errors).
   *
   * @param {Error} error - The error to check
   * @returns {boolean} True if the error should be retried
   * @private
   */
  _isRetryableError(error) {
    // Network errors (no response received)
    if (this.isNetworkError(error)) {
      return true;
    }

    // Check if error has isRetryable flag (SharePointError)
    if (error.isRetryable === true) {
      return true;
    }

    return false;
  }

  /**
   * Analyzes a Response object and returns a structured error if not ok.
   * Returns null if the response is ok.
   *
   * @param {Response} response - The fetch Response object
   * @returns {Promise<SharePointError|null>} Null if ok, enriched SharePointError otherwise
   */
  async checkErrors(response) {
    // Response is ok, no error
    if (response.ok) {
      return null;
    }

    const statusCode = response.status;
    let errorData = null;
    let errorMessage = `HTTP error! status: ${statusCode} ${response.statusText}`;

    // Try to parse the response body to get SharePoint error details
    try {
      const contentType = response.headers.get('Content-Type');
      if (contentType?.includes('application/json')) {
        errorData = await response.json();

        // Extract error message from SharePoint OData error structure
        const spErrorMessage =
          errorData?.['odata.error']?.message?.value ||
          errorData?.error?.message?.value ||
          null;

        if (spErrorMessage) {
          errorMessage = spErrorMessage;
        }
      } else {
        // Try as text
        const textError = await response.text();
        if (textError) {
          errorMessage = textError;
        }
      }
    } catch (parseError) {
      // If we can't parse, use default message
    }

    // Create and return the error object (classification happens in constructor)
    return new SharePointError(errorMessage, statusCode, response, errorData);
  }

  /**
   * Transforms an endpoint to an absolute URL if it's relative.
   *
   * @param {string} endpoint - The endpoint to transform
   * @returns {string} The absolute URL
   */
  _transformEndpoint(endpoint) {
    // Check if endpoint is already absolute
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return endpoint;
    }

    // Remove leading slash if present
    if (endpoint.startsWith('/')) {
      endpoint = endpoint.substring(1);
    }

    // Add _api/ prefix if not present
    if (!endpoint.startsWith('_api/')) {
      endpoint = `_api/${endpoint}`;
    }

    return `${this.siteUrl}/${endpoint}`;
  }

  /**
   * Gets a valid request digest for SharePoint API calls.
   *
   * @param {boolean} [forceRefresh=false] - Whether to force a new digest
   * @returns {Promise<string>} The request digest
   */
  async getRequestDigest(forceRefresh = false) {
    const now = Date.now();

    if (
      !forceRefresh &&
      this._requestDigest &&
      this._digestExpiryTime &&
      now < this._digestExpiryTime
    ) {
      return this._requestDigest;
    }

    console.log('Fetching new Request Digest...');
    const endpoint = '_api/contextinfo';
    const url = this._transformEndpoint(endpoint);

    try {
      const authHeaders = await this.authProvider();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...authHeaders,
          Accept: 'application/json;odata=verbose',
          'Content-Type': 'application/json;odata=verbose',
        },
        body: '',
      });

      const responseJson = await response.json();

      if (
        !response.ok ||
        !responseJson?.d?.GetContextWebInformation?.FormDigestValue
      ) {
        const errorMsg =
          responseJson?.['odata.error']?.message?.value ||
          `HTTP ${response.status}`;
        throw new Error(
          `Failed to process Request Digest response: ${errorMsg}`
        );
      }

      this._requestDigest =
        responseJson.d.GetContextWebInformation.FormDigestValue;
      const timeoutSeconds =
        responseJson.d.GetContextWebInformation.FormDigestTimeoutSeconds ||
        1800;
      this._digestExpiryTime = now + (timeoutSeconds - 120) * 1000;

      return this._requestDigest;
    } catch (error) {
      this._requestDigest = null;
      this._digestExpiryTime = null;
      throw error;
    }
  }

  /**
   * Makes a request to the SharePoint API with automatic retry for network/digest errors.
   *
   * @param {string} endpoint - The API endpoint (relative to /_api/)
   * @param {string} [method='GET'] - The HTTP method
   * @param {object} [options={}] - Additional request options
   * @param {boolean} [options.parseAsText=false] - Whether to return response as text
   * @param {boolean} [options.rawResponse=false] - Whether to return raw response with headers
   * @param {boolean} [options.forceDigestRefresh=false] - Whether to force digest refresh
   * @param {number} [options.maxRetries=3] - Maximum number of retries for network/digest errors
   * @param {object} [options.headers] - Custom headers
   * @param {any} [options.body] - Request body
   * @param {string} [options.credentials] - Credentials option for fetch
   * @param {boolean} [options.batch] - Override batching behavior for this request
   * @param {boolean} [addDigest=false] - Whether to add request digest
   * @returns {Promise<object>} The response data, text, or raw response object
   */
  async request(endpoint, method = 'GET', options = {}, addDigest = false) {
    // Determine if this request should be batched
    const shouldBatch =
      options.batch !== undefined ? options.batch : this.useTransparentBatching;

    // If batching is disabled for this request, execute immediately
    if (!shouldBatch) {
      return this._executeRequest(endpoint, method, options, addDigest);
    }

    // Queue the request for batching
    return new Promise((resolve, reject) => {
      const requestId = crypto.randomUUID();
      const queuedRequest = {
        id: requestId,
        endpoint,
        method,
        options: { ...options },
        addDigest,
        resolve,
        reject,
        batchPart: null,
        partSizeBytes: 0,
      };

      // Pre-build the batch part for this request
      const batchPart = this._buildRequestBatchPart(queuedRequest);
      const partSize =
        _byteLength(batchPart) + BATCH_BOUNDARY_OVERHEAD_PER_PART;

      queuedRequest.batchPart = batchPart;
      queuedRequest.partSizeBytes = partSize;

      // Check if adding this request would exceed limits — flush BEFORE adding
      const estimatedTotalWithNew =
        this._batchSizeBytes + partSize + BATCH_ENVELOPE_OVERHEAD;
      if (
        this._batchQueue.length > 0 &&
        (this._batchQueue.length >= this.maxBatchedOps ||
          estimatedTotalWithNew >= MAX_BATCH_SIZE_BYTES)
      ) {
        if (this._batchTimer) {
          clearTimeout(this._batchTimer);
          this._batchTimer = null;
        }
        this._flushBatchQueue();
      }

      // Add to (now possibly empty) queue
      this._batchQueue.push(queuedRequest);
      this._batchSizeBytes += partSize;

      if (this.debug) {
        console.log(
          `Queued request ${requestId} (${partSize} bytes, total: ${this._batchSizeBytes}): ${method} ${endpoint}`
        );
      }

      // Check again: single request already at limit (edge case for very large requests)
      const currentEstimate = this._batchSizeBytes + BATCH_ENVELOPE_OVERHEAD;
      if (
        this._batchQueue.length >= this.maxBatchedOps ||
        currentEstimate >= MAX_BATCH_SIZE_BYTES
      ) {
        if (this._batchTimer) {
          clearTimeout(this._batchTimer);
          this._batchTimer = null;
        }
        this._flushBatchQueue();
      } else {
        // Set or reset the timer
        if (this._batchTimer) {
          clearTimeout(this._batchTimer);
        }
        this._batchTimer = setTimeout(() => {
          this._batchTimer = null;
          this._flushBatchQueue();
        }, this.batchDelay);
      }
    });
  }

  /**
   * Executes a single request immediately (used when batching is disabled).
   *
   * @param {string} endpoint - The API endpoint
   * @param {string} method - The HTTP method
   * @param {object} options - Request options
   * @param {boolean} addDigest - Whether to add request digest
   * @returns {Promise<object>} The response data
   * @private
   */
  async _executeRequest(endpoint, method, options, addDigest) {
    const maxRetries = options.maxRetries ?? 3;
    let lastError = null;

    // Retry loop for network and digest errors
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this._makeRequest(endpoint, method, options, addDigest);
      } catch (error) {
        lastError = error;

        // Check if this is a retryable error and we have attempts left
        if (this._isRetryableError(error) && attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Exponential backoff, max 5s
          console.warn(
            `Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms: ${method} ${endpoint}`
          );

          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        // Not retryable or out of attempts - throw the error
        throw error;
      }
    }

    // Should not reach here, but just in case
    throw lastError;
  }

  /**
   * Flushes the current batch queue by sending all queued requests.
   *
   * @private
   */
  async _flushBatchQueue() {
    if (this._batchQueue.length === 0) {
      return;
    }

    // Make a copy of the queue and clear it immediately to prevent race conditions
    const requests = [...this._batchQueue];
    this._batchQueue = [];
    this._batchSizeBytes = 0;

    if (this.debug) {
      console.log(`Flushing batch queue with ${requests.length} requests`);
    }

    // If only one request, send it as a single request to avoid batch overhead
    if (requests.length === 1) {
      const request = requests[0];
      try {
        const result = await this._executeRequest(
          request.endpoint,
          request.method,
          request.options,
          request.addDigest
        );
        request.resolve(result);
      } catch (error) {
        request.reject(error);
      }
      return;
    }

    // Multiple requests - send as batch
    try {
      const batchResponse = await this._sendBatchRequest(requests);
      this._processBatchResponse(requests, batchResponse);
    } catch (error) {
      if (this.debug) {
        console.warn(
          'Batch request failed, falling back to individual requests:',
          error.message
        );
      }

      // Fallback: send each request individually
      for (const request of requests) {
        try {
          const result = await this._executeRequest(
            request.endpoint,
            request.method,
            request.options,
            request.addDigest
          );
          request.resolve(result);
        } catch (individualError) {
          request.reject(individualError);
        }
      }
    }
  }

  /**
   * Sends multiple requests as a single batch to SharePoint.
   *
   * @param {Array} requests - Array of queued requests
   * @returns {Promise<object>} The batch response
   * @private
   */
  async _sendBatchRequest(requests) {
    const batchBody = this._buildBatchRequestBody(requests);
    const batchBoundary = batchBody.boundary;

    const batchEndpoint = '_api/$batch';
    const batchOptions = {
      headers: {
        'Content-Type': `multipart/mixed; boundary="${batchBoundary}"`,
      },
      body: batchBody.content,
      rawResponse: true,
    };

    return await this._executeRequest(
      batchEndpoint,
      'POST',
      batchOptions,
      true
    );
  }

  /**
   * Processes a fetch response and handles common response logic.
   *
   * @param {Response} response - The raw fetch response
   * @param {object} options - Request options
   * @returns {Promise<object>} The processed response
   * @private
   */
  async _processResponse(response, options = {}) {
    // For rawResponse mode: only throw if it's a retryable error (for retry logic)
    // Otherwise return raw response and let caller handle errors
    if (options.rawResponse) {
      if (!response.ok) {
        const error = await this.checkErrors(response.clone());
        if (error && error.isRetryable) {
          throw error;
        }
      }
      return response;
    }

    // Standard mode: always check and throw on error
    const error = await this.checkErrors(response.clone());
    if (error) {
      throw error;
    }

    // Process successful response
    let responseJson = {};
    let responseText = '';

    try {
      if (response.headers.get('Content-Type')?.includes('multipart/mixed')) {
        responseText = await response.text();
        responseJson = { rawBatchResponse: responseText };
      } else {
        const contentLength = response.headers.get('Content-Length');
        const isNoContentStatus =
          response.status === 204 || response.status === 304;
        const hasNoContent = contentLength === '0' || isNoContentStatus;

        if (hasNoContent) {
          if (options.parseAsText) {
            return { textResponse: '' };
          }
          responseJson = {};
        } else {
          if (options.parseAsText) {
            responseText = await response.text();
            if (this.debug) console.log('received text', responseText);
            return { textResponse: responseText };
          }
          responseJson = await response.json();
        }
      }
    } catch (parseError) {
      if (response.ok && !options.parseAsText) {
        console.error('Failed to parse JSON response:', parseError);
        try {
          responseText = await response.text();
        } catch (e) {
          /* ignore */
        }
        throw new Error(
          `Failed to parse successful JSON response. Content: ${responseText || 'N/A'}`
        );
      }
    }

    return responseJson;
  }

  /**
   * Internal method that performs the actual HTTP request.
   *
   * @param {string} endpoint - The API endpoint
   * @param {string} method - The HTTP method
   * @param {object} options - Request options
   * @param {boolean} addDigest - Whether to add request digest
   * @returns {Promise<object>} The response data or text
   * @private
   */
  async _makeRequest(endpoint, method, options, addDigest) {
    // console.log("request", options)
    const url = this._transformEndpoint(endpoint);
    let authHeaders = {};
    const digestHeader = {};

    try {
      authHeaders = await this.authProvider();
    } catch (authError) {
      console.error('Authentication provider failed:', authError);
      throw new Error(`Authentication failed: ${authError.message}`);
    }

    if (addDigest) {
      const digest = await this.getRequestDigest(options.forceDigestRefresh);
      digestHeader['X-RequestDigest'] = digest;
    }

    const defaultHeaders = { Accept: 'application/json;odata=verbose' };
    const requestOptions = {
      method,
      headers: {
        ...defaultHeaders,
        ...authHeaders,
        ...digestHeader,
        ...(options.headers || {}),
      },
      body: options.body,
      credentials: options.credentials || 'include',
    };

    if (requestOptions.body && !requestOptions.headers['Content-Type']) {
      if (typeof requestOptions.body === 'string') {
        requestOptions.headers['Content-Type'] =
          'application/json;odata=verbose';
      }
    }

    try {
      const response = await fetch(url, requestOptions);
      return await this._processResponse(response, options);
    } catch (error) {
      // Handle digest expiration here before retry
      if (error.isDigestError) {
        console.warn('Request Digest expired. Clearing cached digest.');
        this._requestDigest = null;
        this._digestExpiryTime = null;
      }
      throw error;
    }
  }

  /**
   * Builds the batch part for a single request.
   * For write operations, includes changeset wrapper.
   * For read operations, includes multipart headers.
   *
   * @param {object} request - The queued request object
   * @returns {string} The pre-built batch part content
   * @private
   */
  _buildRequestBatchPart(request) {
    const method = request.method.toUpperCase();
    const isWriteOperation = [
      'POST',
      'PATCH',
      'MERGE',
      'DELETE',
      'PUT',
    ].includes(method);

    if (isWriteOperation) {
      const changesetBoundary = `changeset_${crypto.randomUUID()}`;
      const httpRequestLines = this._buildHttpRequestPart(request, false);

      const changesetParts = [
        `--${changesetBoundary}`,
        'Content-Type: application/http',
        'Content-Transfer-Encoding: binary',
        '',
        ...httpRequestLines,
        `--${changesetBoundary}--`,
      ];

      const changesetContent = changesetParts.join('\r\n');

      return [
        `Content-Type: multipart/mixed; boundary="${changesetBoundary}"`,
        'Content-Transfer-Encoding: binary',
        '',
        changesetContent,
      ].join('\r\n');
    } else {
      // Read operations - return HTTP part directly
      const httpRequestPart = this._buildHttpRequestPart(request, true);
      return httpRequestPart.join('\r\n');
    }
  }

  /**
   * Builds the multipart batch request body using pre-built request parts.
   *
   * @param {Array} requests - Array of queued requests with pre-built batch parts
   * @returns {object} Object with boundary and content properties
   * @private
   */
  _buildBatchRequestBody(requests) {
    const batchBoundary = `batch_${crypto.randomUUID()}`;
    const parts = [];

    for (const request of requests) {
      if (!request.batchPart) {
        throw new Error(
          `Request ${request.id} missing pre-built batch part. ` +
            `This should not happen - parts are built when queued.`
        );
      }
      parts.push(`--${batchBoundary}`, request.batchPart);
    }

    // Close the batch
    parts.push(`--${batchBoundary}--`, '');

    const content = parts.join('\r\n');

    if (this.debug) {
      console.log(`Built batch request body: ${content.length} bytes`);
    }

    return {
      boundary: batchBoundary,
      content,
    };
  }

  /**
   * Builds an individual HTTP request part for inclusion in a batch.
   * For GET requests, this includes the multipart headers.
   * For write operations, this returns just the HTTP request (headers are added in changeset).
   *
   * @param {object} request - The request object
   * @param {boolean} includeMultipartHeaders - Whether to include Content-Type and Content-Transfer-Encoding headers
   * @returns {Array} Array of lines for the HTTP request part
   * @private
   */
  _buildHttpRequestPart(request, includeMultipartHeaders = true) {
    const { endpoint, method, options } = request;
    const url = this._transformEndpoint(endpoint);

    const lines = [];

    // Add multipart headers for direct batch requests (GET operations)
    if (includeMultipartHeaders) {
      lines.push(
        'Content-Type: application/http',
        'Content-Transfer-Encoding: binary',
        ''
      );
    }

    // Add the HTTP request line
    lines.push(`${method.toUpperCase()} ${url} HTTP/1.1`);

    // Add headers
    const headers = {
      Accept: 'application/json;odata=verbose',
      ...(options.headers || {}),
    };

    // Add Content-Type for requests with body
    if (options.body && !headers['Content-Type']) {
      if (typeof options.body === 'string') {
        headers['Content-Type'] = 'application/json;odata=verbose';
      }
    }

    for (const [key, value] of Object.entries(headers)) {
      lines.push(`${key}: ${value}`);
    }

    // Add empty line before body
    lines.push('');

    // Add body if present
    if (options.body) {
      lines.push(options.body);
    }

    return lines;
  }

  /**
   * Processes the batch response and resolves/rejects individual request promises.
   *
   * @param {Array} requests - Array of original queued requests
   * @param {Response} batchResponse - The raw batch response
   * @private
   */
  async _processBatchResponse(requests, batchResponse) {
    try {
      const responseText = await batchResponse.text();
      const parsedResponses = this._parseBatchResponse(
        responseText,
        batchResponse.headers.get('Content-Type')
      );

      // Create synthetic Response objects and process them using the same logic as individual requests
      for (let i = 0; i < requests.length; i++) {
        const request = requests[i];
        const parsedResponse = parsedResponses[i];

        if (!parsedResponse) {
          request.reject(
            new Error(`No response found for request ${request.id}`)
          );
          continue;
        }

        try {
          // Create a synthetic Response object that behaves like a real fetch Response
          const syntheticResponse = new Response(parsedResponse.body, {
            status: parsedResponse.status,
            statusText: this._getStatusText(parsedResponse.status),
            headers: new Headers(parsedResponse.headers),
          });

          // Use the same _processResponse logic as individual requests
          const result = await this._processResponse(
            syntheticResponse,
            request.options
          );
          request.resolve(result);
        } catch (error) {
          request.reject(error);
        }
      }
    } catch (error) {
      // If we can't parse the batch response, reject all requests
      for (const request of requests) {
        request.reject(
          new Error(`Failed to process batch response: ${error.message}`)
        );
      }
    }
  }

  /**
   * Gets the standard HTTP status text for a status code.
   *
   * @param {number} status - HTTP status code
   * @returns {string} Status text
   * @private
   */
  _getStatusText(status) {
    const statusTexts = {
      200: 'OK',
      201: 'Created',
      202: 'Accepted',
      204: 'No Content',
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      409: 'Conflict',
      500: 'Internal Server Error',
      502: 'Bad Gateway',
      503: 'Service Unavailable',
    };
    return statusTexts[status] || 'Unknown';
  }

  /**
   * Parses a multipart batch response into individual response objects.
   * Returns responses in the same order as they appear in the batch response.
   *
   * @param {string} responseText - The raw multipart response text
   * @param {string} contentType - The Content-Type header from the response
   * @returns {Array} Array of parsed response objects in order
   * @private
   */
  _parseBatchResponse(responseText, contentType) {
    // Extract boundary from Content-Type header
    const boundaryMatch = contentType.match(/boundary=(.+?)(?:;|$)/);
    if (!boundaryMatch) {
      throw new Error('Could not find boundary in batch response');
    }

    const boundary = boundaryMatch[1].replace(/"/g, '');
    const parts = responseText.split(`--${boundary}`);

    const responses = [];

    for (const part of parts) {
      if (!part.trim() || part.trim() === '--') continue;

      // Parse this part
      const lines = part.split('\r\n');
      let inHttpResponse = false;
      const httpResponseLines = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Look for the start of HTTP response
        if (line.startsWith('HTTP/1.1')) {
          inHttpResponse = true;
        }

        if (inHttpResponse) {
          httpResponseLines.push(line);
        }
      }

      if (httpResponseLines.length > 0) {
        const response = this._parseHttpResponse(httpResponseLines);
        responses.push(response);
      }
    }

    return responses;
  }

  /**
   * Parses an individual HTTP response from batch response lines.
   *
   * @param {Array} lines - Array of HTTP response lines
   * @returns {object} Parsed response object
   * @private
   */
  _parseHttpResponse(lines) {
    const statusLine = lines[0];
    const statusMatch = statusLine.match(/HTTP\/1\.1 (\d+)/);
    const status = statusMatch ? parseInt(statusMatch[1]) : 500;

    let headerEndIndex = 1;
    const headers = {};

    // Parse headers
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim() === '') {
        headerEndIndex = i;
        break;
      }
      const colonIndex = lines[i].indexOf(':');
      if (colonIndex > 0) {
        const key = lines[i].substring(0, colonIndex).trim();
        const value = lines[i].substring(colonIndex + 1).trim();
        headers[key] = value;
      }
    }

    // Parse body
    const bodyLines = lines.slice(headerEndIndex + 1);
    const body = bodyLines.join('\r\n').trim();

    return {
      status,
      headers,
      body: body || null, // Return null for empty bodies instead of empty string
    };
  }

  /**
   * Auto-detects the SharePoint site URL from the current browser context
   * @returns {string|null} The detected site URL or null if not in SharePoint context
   * @private
   */
  _autoDetectSiteUrl() {
    if (
      typeof window === 'undefined' ||
      typeof window.location === 'undefined'
    ) {
      return null; // Not in browser context
    }

    const pathname = window.location.pathname;

    // Check if the pathname contains "/sites/"
    if (pathname.includes('/sites/')) {
      // Split the pathname by "/sites/" and take the first part
      const parts = pathname.split('/sites/');
      if (parts.length > 1) {
        // Reconstruct the URL up to the level after "sites"
        const baseUrl =
          window.location.origin + '/sites/' + parts[1].split('/')[0];
        return baseUrl;
      }
    }

    // If the pathname does not contain "/sites/", return the origin
    return window.location.origin;
  }
}
